// src/features/apps/useStoreAppCheck.ts

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { STORE_APP_CHECK_QUERY_KEYS } from '@/lib/constants/storeAppCheck';
import { AppFactoryApi } from './AppFactoryApi';
import { StoreAppCheckUtils } from './StoreAppCheckUtils';

/**
 * The farm's verdict on the store app id saved for one platform of an app
 * (appfactory ADR-0029). Asked only when the platform has a check and the row
 * carries an id. Like every factory read it degrades quietly — no retry, and an
 * error (a daemon older than the check answers 404) just means no line. While
 * a check is in flight it re-reads every few seconds, for a bounded time
 * (StoreAppCheckUtils.pollInterval); after that a later visit re-reads.
 */
export function useStoreAppCheck(
  appCfgId: number,
  platform: string,
  storeAppId: string | null | undefined,
) {
  const { selectedCId } = useAuth();
  const id = (storeAppId ?? '').trim();
  // When this mount first read a check in flight; null = none in flight.
  const firstInFlightAt = useRef<number | null>(null);
  useEffect(() => {
    firstInFlightAt.current = null;
  }, [selectedCId, appCfgId, platform, id]);

  return useQuery({
    queryKey: STORE_APP_CHECK_QUERY_KEYS.check(selectedCId ?? 0, appCfgId, platform, id),
    queryFn: () => AppFactoryApi.getStoreAppCheck(selectedCId!, appCfgId, platform),
    enabled:
      selectedCId != null && appCfgId > 0 && StoreAppCheckUtils.shouldAsk(platform, storeAppId),
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const inFlight = StoreAppCheckUtils.isInFlight(query.state.data);
      const now = Date.now();
      if (!inFlight) firstInFlightAt.current = null;
      else if (firstInFlightAt.current == null) firstInFlightAt.current = now;
      return StoreAppCheckUtils.pollInterval(inFlight, firstInFlightAt.current, now);
    },
  });
}
